/**
 * Customer authentication and management utilities
 * Handles customer access tokens, login, and profile updates via Storefront API
 */

import { customerToken, UPDATE_CUSTOMER_DETAILS, CUSTOMER_ACCESS_TOKEN_CREATE } from 'libs/storefront/customer.query'
import { storefront } from 'libs/storefront/client'
import { CustomerTokenPayload } from './customer.types'
const CUSTOMER_ACCESS_KEY = 'customer_access_token'

/**
 * Sets the customer access token cookie
 */
function setCustomerAccessCookie(accessToken: string, expiresAt: string | Date) {
  const d = new Date(expiresAt)
  const expires = 'expires=' + d.toUTCString()
  document.cookie = CUSTOMER_ACCESS_KEY + '=' + accessToken + ';' + expires
}

/**
 * Returns the customer access token cookie
 */
function getCustomerAccessCookie() {
  const cookieToken = document.cookie.split(';').find((t) => t.includes(`${CUSTOMER_ACCESS_KEY}=`))
  if (!cookieToken) return
  return cookieToken.split('=').pop()
}

interface CreateCustomerTokenResponse {
  data: {
    customerAccessTokenCreate: {
      customerAccessToken: {
        accessToken: string
        expiresAt: string
      }
    }
  }
}

/**
 *
 * For any Customer update functions that Shopify does not support
 * natively with its {% form %} tags. We have to fetch the user's
 * access token using the Shopify StoreFrontApi.
 *
 * @param {\} email
 * @param {*} password
 */
async function getCustomerAccessToken(email: string, password: string) {
  /** Check if cookie exists / has not expired */
  const hasCustomerToken = getCustomerAccessCookie()
  if (hasCustomerToken) return hasCustomerToken

  /** Create customer access token */
  const customerCreateTokenResponse = await storefront.mutate({
    mutation: CUSTOMER_ACCESS_TOKEN_CREATE,
    variables: {
      input: { email, password }
    }
  }) as CreateCustomerTokenResponse

  /** Pull Access token from GraphQL response */
  const token = customerToken(customerCreateTokenResponse)

  /** Set token in cookie with expiry */
  setCustomerAccessCookie(token.accessToken, token.expiresAt)
  return token.accessToken
}

/**
 *
 * @param {email: string, password: string, firstName: string, lastName: string, phone: string,company: string} payload
 */
async function updateCustomerDetails(customerAccessToken: string, payload: CustomerTokenPayload, emailRef: string) {
  /** fetch token */

  /** Don't pass the email in the payload if the value is the same */
  if (payload.email === emailRef) {
    delete payload.email
  }
  /** Update customer details */
  await storefront.mutate({
    mutation: UPDATE_CUSTOMER_DETAILS,
    variables: {
      customerAccessToken,
      customer: payload
    }
  })

  /** Handle response */
  successfulResponse()
}

/**
 *
 */
async function customerPasswordRecover(email: string) {
  if (!email) return
  /** Update customer details */
  await storefront.mutate({
    mutation: UPDATE_CUSTOMER_DETAILS,
    variables: {
      email
    }
  })
}

/**
 *
 */
async function customerPasswordReset() {}

/**
 * Deletes the customer access token cookie from browser context.
 */
function customerLogout() {
  /** Detail the token */
  setCustomerAccessCookie('', new Date())
  window.location.href = '/account/logout'
}

function successfulResponse() {
  window.location.reload()
}

export { updateCustomerDetails, getCustomerAccessToken, customerLogout, customerPasswordRecover, customerPasswordReset }
